import { QuerySelectExpr } from '@apis/Customers/model';
import { BillingInvoice, BillingInvoiceAdjustment, FocusFieldInfo, Query } from '@apis/Invoices/model';
import { QueryResult } from '@apis/Resources';
import { ActionIcon, Box, Group, NumberInput, Select, TextInput, Tooltip } from '@mantine/core';
import { Clearfix, TooltipWhite } from '@root/Design/Primitives';
import {
    SettingsInputRow,
    SettingsLabel,
    SettingsSectionItemBody,
    SettingsSectionItemHeader,
    SettingsSectionItemHeaderLabel,
} from '@root/Design/Settings';
import { useFmtSvc } from '@root/Services/FormatService';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, InfoCircle } from 'tabler-icons-react';
import { queryBuilder, SchemaService, SchemaValueProvider } from '@root/Services/QueryExpr';
import { NamedFilterSetModel } from '../Filter/FilterSetEditor/NamedFilterSetModel';
import { FilterSetDataFilter } from '../Filter/FilterSetEditor/FilterSetEditor';
import { useFilterValueProviders } from '../Invoices/InvoiceFilterComponents';
import { FillerSwitch } from '@root/Design/Filler';
import { useDi, useDiMemo } from '@root/Services/DI';
import { endOfMonth } from 'date-fns';
import { QueryDescriptorService } from '../Filter/Services';
import { InvoiceApiService } from '@root/Services/Invoices/InvoiceApiService';
import { InvoiceSchemaService } from '@root/Services/Invoices/InvoiceSchemaService';
import { EventEmitter, useEvent, useToggle } from '@root/Services/EventEmitter';
import { FocusMetadataService } from '@root/Services/Invoices/FocusMetadataService';
import { DatePicker } from '@mantine/dates';
import { Combobox } from '../Picker/Combobox';
import { DatasourceSchemaContext } from '../Filter/DatasourceContext';
import { QueryDatasourceCollection } from '@root/Services/Query/QueryDatasourceCollection';

export function BillingInvoiceAdjustmentFields(props: {
    invoice: BillingInvoice;
    fields: QuerySelectExpr[] | null;
    onChange: (fields: QuerySelectExpr[] | null) => void;
}) {
    const focusFieldSvc = useDi(FocusMetadataService);
    const [inputFields, setInputFields] = useState<{ focusField: FocusFieldInfo; selectExpr: QuerySelectExpr; mandatory: number }[]>([]);

    useEffect(() => {
        (async () => {
            const fieldsMetadata = await focusFieldSvc.getFocusMetadata();

            const ignoredFields = [
                'BilledCost',
                'EffectiveCost',
                'ContractedCost',
                'ChargePeriodStart',
                'ChargePeriodEnd',
                'BillingPeriodStart',
                'BillingPeriodEnd',
            ];

            const copyOnlyFields = ['BillingAccountId', 'CloudPlatform', 'ServiceCategory', 'BilledDate', 'BillingAccountName', 'NativeLineItemType'];
            const focusFields = fieldsMetadata
                .getFields()
                .filter((f) => !f.Ignore)
                .filter((f) => !ignoredFields.includes(f.Field!));

            const fieldsByBaseName = new Map<string, QuerySelectExpr[]>();

            // Group fields by their base name (everything before the last underscore and number)
            (props.fields ?? []).forEach((field) => {
                const alias = field.Alias!;
                // Check if this looks like a generated alias (ends with _number)
                const match = alias.match(/^(.+)_\d+$/);
                const baseName = match ? match[1] : alias;

                if (!fieldsByBaseName.has(baseName)) {
                    fieldsByBaseName.set(baseName, []);
                }
                fieldsByBaseName.get(baseName)!.push(field);
            });

            const inputFields: { focusField: FocusFieldInfo; selectExpr: QuerySelectExpr; mandatory: number }[] = [];

            focusFields.forEach((focusField) => {
                const fieldName = focusField.Field!;
                const fieldsForThisType = fieldsByBaseName.get(fieldName) ?? [];

                if (fieldsForThisType.length > 0) {
                    // Add entries for all existing fields of this type
                    fieldsForThisType.forEach((selectExpr) => {
                        inputFields.push({
                            focusField,
                            selectExpr,
                            mandatory: focusField.FeatureLevel === 'Mandatory' ? 0 : 1,
                        });
                    });
                } else {
                    const selectExpr = {
                        Expr: copyOnlyFields.includes(fieldName) ? { Field: fieldName } : undefined,
                        Alias: fieldName,
                    };
                    inputFields.push({
                        focusField,
                        selectExpr,
                        mandatory: focusField.FeatureLevel === 'Mandatory' ? 0 : 1,
                    });
                }
            });

            inputFields.sort((a, b) => {
                return a.mandatory - b.mandatory || a.focusField.DisplayName!.localeCompare(b.focusField.DisplayName!);
            });
            setInputFields(inputFields);
        })();
    }, []);

    const handleFieldChange = (index: number, field?: QuerySelectExpr) => {
        const updatedFields = inputFields.map((inputField, i) => {
            if (i === index) {
                return {
                    ...inputField,
                    selectExpr: field ?? inputField.selectExpr,
                };
            }
            return inputField;
        });
        setInputFields(updatedFields);
        props.onChange(updatedFields.map((inputField) => inputField.selectExpr));
    };

    const [collapsed, { toggle }] = useToggle(false);
    const ExpanderIcon = collapsed ? ChevronRight : ChevronDown;

    return (
        <>
            <SettingsSectionItemHeader>
                <div style={{ width: '100%' }}>
                    <Group spacing={4} sx={{ float: 'left' }}>
                        <TooltipWhite
                            withinPortal
                            multiline
                            label={`(Optional) Add filters to limit which invoice line items are applicable for this tile.`}
                        >
                            <ActionIcon>
                                <InfoCircle size={16} />
                            </ActionIcon>
                        </TooltipWhite>
                        <SettingsSectionItemHeaderLabel>Overwrite Fields for Copied Line Items</SettingsSectionItemHeaderLabel>
                    </Group>

                    <ActionIcon onClick={toggle} variant="transparent">
                        <ExpanderIcon size={16} />
                    </ActionIcon>
                </div>
            </SettingsSectionItemHeader>
            <SettingsSectionItemBody hidden={collapsed}>
                {inputFields.map((inputField, index) => (
                    <FocusFieldInput
                        invoice={props.invoice}
                        key={`${inputField.focusField.Field}_${index}`}
                        focusField={inputField.focusField}
                        selectExpr={inputField.selectExpr}
                        onChange={(field) => handleFieldChange(index, field)}
                    />
                ))}
            </SettingsSectionItemBody>
        </>
    );
}

function FocusFieldInput(props: {
    invoice: BillingInvoice;
    focusField: FocusFieldInfo;
    selectExpr: QuerySelectExpr;
    onChange: (field: QuerySelectExpr) => void;
}) {
    const { focusField, selectExpr, onChange } = props;
    const invoiceApi = useDiMemo(InvoiceApiService);
    const fmtSvc = useFmtSvc();
    const copyOnlyFields = ['BillingAccountId', 'CloudPlatform', 'ServiceCategory', 'BilledDate', 'BillingAccountName', 'NativeLineItemType'];
    const options = copyOnlyFields.includes(focusField.Field!) ? ['Copy', 'Override'] : ['Blank', 'Copy', 'Override'];
    const inputModel = useMemo(() => {
        const changed = EventEmitter.empty();
        const result = {
            get isField() {
                return selectExpr.Expr !== undefined && selectExpr.Expr !== null ? 'Field' in selectExpr.Expr! : false;
            },
            get isOverride() {
                return selectExpr.Expr !== undefined && selectExpr.Expr !== null ? 'Value' in selectExpr.Expr! : false;
            },
            get isBlank() {
                return selectExpr.Expr === undefined || selectExpr.Expr === null;
            },
            changed,
            handleSelect: (value: string) => {
                if (value === 'Override') {
                    selectExpr.Expr = { Value: focusField.IndexerType === 'float' ? 0 : focusField.IndexerType === 'date' ? new Date() : '' };
                } else if (value === 'Copy') {
                    selectExpr.Expr = { Field: focusField.Field };
                } else if (value === 'Blank') {
                    selectExpr.Expr = undefined;
                }
                changed.emit();
                onChange(selectExpr);
            },
            get value() {
                let rawValue = selectExpr?.Expr?.Value;
                if (focusField.IndexerType === 'date') {
                    rawValue ??= new Date();
                    return rawValue instanceof Date ? rawValue : fmtSvc.toLocalDate(rawValue);
                } else if (focusField.IndexerType === 'float') {
                    rawValue ??= 0;
                    return parseFloat(rawValue);
                } else {
                    rawValue ??= '';
                    return rawValue;
                }
            },
            updateValue: (value: any) => {
                if (result.isOverride) {
                    selectExpr.Expr = { Value: value };
                    changed.emit();
                    onChange(selectExpr);
                }
            },
        };
        return result;
    }, [selectExpr]);

    useEvent(inputModel.changed);
    const inputType = (focusField.IndexerType ?? null) as 'string' | 'date' | 'float' | null;
    const required = focusField.FeatureLevel === 'Mandatory';
    useEffect(() => {
        onChange(selectExpr);
    }, []);

    const stringOptions = useMemo(() => {
        if (inputType !== 'string') {
            return async () => [];
        }
        if (!inputModel.isOverride) {
            return async () => [];
        }
        var invoiceDate = new Date(props.invoice.Year!, props.invoice.Month! - 1, 1);

        return async () => {
            const results = await queryBuilder<Record<string, string>>()
                .select((b) => ({
                    count: b.count(),
                    value: b.model[focusField.Field!],
                }))
                .execute((q) => invoiceApi.queryMonthlyRollup(q, [invoiceDate]));

            return (results.Results ?? [])
                .sort((a, b) => a.value.localeCompare(b.value, undefined, { sensitivity: 'base' }))
                .map((item) => ({ value: item.value }));
        };
    }, [inputModel.isOverride]);

    return (
        <SettingsInputRow>
            <Tooltip label={focusField.Description} withinPortal width={300} multiline>
                <div>
                    <SettingsLabel>
                        <span>
                            {focusField.DisplayName} {required ? <span style={{ color: 'red' }}>*</span> : null}
                        </span>
                    </SettingsLabel>
                </div>
            </Tooltip>
            <Group noWrap>
                <div style={{ width: 200 }}>
                    {inputModel.isField || inputModel.isBlank ? (
                        <TextInput value={focusField.DisplayName ?? ''} disabled />
                    ) : inputType === 'string' ? (
                        <Combobox
                            options={stringOptions}
                            lazyLoad
                            pickerProps={{ inlineFullText: true }}
                            value={inputModel.value}
                            onChange={(e) => inputModel.updateValue(e)}
                        />
                    ) : inputType === 'date' ? (
                        <DatePicker value={inputModel.value} required={required} onChange={inputModel.updateValue} />
                    ) : inputType === 'float' ? (
                        <NumberInput value={inputModel.value} required={required} onChange={inputModel.updateValue} />
                    ) : (
                        <>Input not supported for field {focusField.DisplayName}</>
                    )}
                </div>

                <Select
                    sx={{ width: 125 }}
                    data={options}
                    value={inputModel.isOverride ? 'Override' : inputModel.isField ? 'Copy' : 'Blank'}
                    defaultValue={copyOnlyFields.includes(focusField.Field!) ? 'Copy' : 'Blank'}
                    onChange={(value) => inputModel.handleSelect(value!)}
                />
            </Group>
        </SettingsInputRow>
    );
}

interface ApplyToSectionProps {
    billingInvoice: BillingInvoice;
    adjustment: BillingInvoiceAdjustment;
    showCollapseHeader?: boolean;
    onChanges?: (adjustment: Partial<BillingInvoiceAdjustment>) => void;
}

export function ApplyToSection(props: ApplyToSectionProps) {
    const { billingInvoice, adjustment, showCollapseHeader } = props;
    const [schemaCtx, setSchemaCtx] = useState<DatasourceSchemaContext | null>(null);
    const invoiceApi = useDiMemo(InvoiceApiService);
    const invoiceSchemaSvc = useDiMemo(InvoiceSchemaService);
    const datasources = useDiMemo(QueryDatasourceCollection);
    const [datasource, setDatasource] = useState<{ datasource: <T>(query: Query) => Promise<QueryResult<T>> } | null>(null);

    useEffect(() => {
        (async () => {
            const invoiceMonth = new Date(billingInvoice.Year ?? 0, (billingInvoice.Month ?? 0) - 1, 1);
            const monthEnd = endOfMonth(invoiceMonth);
            const datasourceApi: <T>(query: Query) => Promise<QueryResult<T>> = (q) =>
                invoiceApi.query(q, { from: invoiceMonth, to: monthEnd }, false);
            const datasource = await datasources.getDatasource('cur', datasourceApi, true);
            const types = await invoiceSchemaSvc.getDailySchema();
            const schemaSvc = SchemaService.create(types);
            const queryDescriptorSvc = QueryDescriptorService.create(schemaSvc);

            setSchemaCtx({ schemaSvc, queryDescriptorSvc, valueProvider: new SchemaValueProvider(schemaSvc, datasourceApi), datasource });
            setDatasource({ datasource: datasourceApi });
        })();
    }, []);

    return (
        <FillerSwitch loading={!schemaCtx || !datasource}>
            {() => (
                <CompanyApplyToSection
                    adjustment={adjustment}
                    billingInvoice={billingInvoice}
                    datasource={datasource?.datasource!}
                    schemaCtx={schemaCtx!}
                    showCollapseHeader={showCollapseHeader}
                    onChanges={props.onChanges}
                />
            )}
        </FillerSwitch>
    );
}

function CompanyApplyToSection(
    props: ApplyToSectionProps & { schemaCtx: DatasourceSchemaContext; datasource: <T>(query: Query) => Promise<QueryResult<T>> }
) {
    const { adjustment, schemaCtx, datasource, showCollapseHeader } = props;

    const filterSetModel = useMemo(() => NamedFilterSetModel.create(adjustment, 'ApplyTo', true), []);
    const applyToFilter = useMemo(() => {
        const result = filterSetModel.inclusionRules.getOrAdd();
        const operationNames = new Map([
            ['ExistingAmount', 'Line Items to Adjust'],
            ['ExistingPercent', 'Line Items to Adjust'],
            ['NewPercentOfLineItems', 'Copy & Derive Total from Line Items'],
            ['NewPercentOfTotal', 'Copy from Line Items'],
            ['OmitLineItems', 'Line Items to Omit'],
            ['OverrideItems', 'Line Items to Override'],
        ]);
        const name = operationNames.get(adjustment.AdjustmentOperation ?? '');
        if (name) {
            result.setName(name);
        }
        return result;
    }, [filterSetModel]);

    const valueRendererProvider = useFilterValueProviders(datasource);
    const sectionItemProps = {
        datasource,
        namedFilter: applyToFilter,
        canRemove: false,
        schemaSvc: schemaCtx.schemaSvc,
        fieldInfoProvider: schemaCtx.queryDescriptorSvc.fieldInfoProvider,
        valueProvider: schemaCtx.valueProvider,
        valueRendererProvider,
        renderSectionItem: false,
    };

    useEvent(
        filterSetModel.onFilterChanged,
        useCallback(() => {
            props.onChanges?.({ ApplyTo: filterSetModel.filterSet });
        }, [applyToFilter])
    );

    const name = applyToFilter.getName();
    const [collapsed, { toggle }] = useToggle(false);
    const ExpanderIcon = collapsed ? ChevronRight : ChevronDown;

    return (
        <>
            {showCollapseHeader && (
                <SettingsSectionItemHeader>
                    <div style={{ width: '100%' }}>
                        <Group spacing={4} sx={{ float: 'left' }}>
                            <TooltipWhite withinPortal multiline label={`Add filters to target line items with this adjustment`}>
                                <ActionIcon>
                                    <InfoCircle size={16} />
                                </ActionIcon>
                            </TooltipWhite>
                            <SettingsSectionItemHeaderLabel> {name}</SettingsSectionItemHeaderLabel>
                        </Group>
                        <ActionIcon sx={{ float: 'right' }} onClick={toggle} variant="transparent">
                            <ExpanderIcon size={16} />
                        </ActionIcon>
                        <Clearfix />
                    </div>
                </SettingsSectionItemHeader>
            )}
            <Box px={12} py={12} hidden={collapsed}>
                <FilterSetDataFilter {...sectionItemProps} />
            </Box>
        </>
    );
}
